import { useCallback, useEffect, useState } from "react";
import { chatbotApi, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, Mail, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";

// BC-020: invite teammates to share a chatbot. Three roles:
// Viewer sees leads and conversations only. Agent can also reply to chats
// and update leads. Co-owner can also edit settings and training. None of
// them can see API keys, delete the chatbot, or invite others.
const STATUS = {
  active: { label: "Active", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  pending: { label: "Invitation sent", cls: "text-amber-700 bg-amber-50 border-amber-200" },
};

// BC-020: role options for the invite form and per-row selects.
const ROLE_OPTIONS = [
  { value: "viewer", label: "Viewer: see leads and conversations only" },
  { value: "agent", label: "Agent: also reply to chats and update leads" },
  { value: "co_owner", label: "Co-owner: also edit settings and training" },
];

const ROLE_LABELS = { viewer: "Viewer", agent: "Agent", co_owner: "Co-owner" };

export default function CoOwnersPanel({ chatbotId }) {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await chatbotApi.listCoOwners(chatbotId);
      setData(res.data);
    } catch (e) {
      setData({ error: formatApiError(e?.response?.data?.detail) });
    }
  }, [chatbotId]);

  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, ok) => {
    if (busy) return;
    setBusy(key);
    try {
      await fn();
      if (ok) toast.success(ok);
      await load();
      return true;
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
      await load();
      return false;
    } finally {
      setBusy("");
    }
  };

  const invite = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    const done = await run("invite", () => chatbotApi.inviteCoOwner(chatbotId, value, role),
      "Invitation sent. They get the chosen role once they accept the email link.");
    if (done) setEmail("");
  };

  if (!data) {
    return (
      <div className="border border-border p-6 text-sm text-muted-foreground flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading shared access
      </div>
    );
  }
  if (data.error) {
    return <div className="border border-border p-6 text-sm text-muted-foreground">{data.error}</div>;
  }

  const list = data.co_owners || [];
  const max = data.max || 10;
  const full = list.length >= max;

  return (
    <div className="border border-border p-6 space-y-5" data-testid="co-owners-panel">
      <div>
        <h2 className="font-semibold">Shared access</h2>
        <p className="text-sm text-muted-foreground">
          Invite teammates to work on this chatbot with you. Viewer: see leads and conversations only. Agent: also
          reply to chats and update leads. Co-owner: also edit settings and training. None of them can see your API
          keys or delete the chatbot. The chatbot stays in your plan and uses your API keys.
        </p>
      </div>

      {!data.email_configured && (
        <p className="text-sm border border-amber-200 bg-amber-50 text-amber-800 p-3">
          Email sending is not configured on this server, so invitations cannot be sent yet.
        </p>
      )}

      <div className="space-y-2">
        <p className="font-medium text-sm">People with access ({list.length}/{max})</p>
        {list.length > 0 && (
          <ul className="divide-y divide-border border border-border">
            {list.map((r) => {
              const st = STATUS[r.status] || STATUS.pending;
              return (
                <li key={r.id} className="flex flex-wrap items-center gap-3 p-3">
                  <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="text-sm break-all flex-1 min-w-[160px]">{r.email}</span>
                  <label className="sr-only" htmlFor={`role-${r.id}`}>Role for {r.email}</label>
                  <select
                    id={`role-${r.id}`}
                    value={r.role || "co_owner"}
                    disabled={!!busy}
                    className="text-sm border border-border bg-background px-2 py-1 rounded-none"
                    onChange={(e) =>
                      run(`role-${r.id}`, () => chatbotApi.changeShareRole(chatbotId, r.id, e.target.value), "Role updated")
                    }
                  >
                    {ROLE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{ROLE_LABELS[o.value]}</option>
                    ))}
                  </select>
                  <span className={`text-xs border px-2 py-0.5 ${st.cls}`}>{st.label}</span>
                  {r.status === "pending" && (
                    <Button
                      type="button" variant="outline" size="sm" className="rounded-none"
                      disabled={!!busy}
                      onClick={() => run(`resend-${r.id}`, () => chatbotApi.resendCoOwner(chatbotId, r.id), "Invitation sent again")}
                    >
                      {busy === `resend-${r.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      <span className="ml-1">Resend</span>
                    </Button>
                  )}
                  <Button
                    type="button" variant="outline" size="sm" className="rounded-none"
                    disabled={!!busy}
                    aria-label={`Remove ${r.email}`}
                    onClick={() => {
                      if (!window.confirm(`Remove ${r.email} from this chatbot? Their access ends immediately.`)) return;
                      run(`del-${r.id}`, () => chatbotApi.removeCoOwner(chatbotId, r.id), "Access removed");
                    }}
                  >
                    {busy === `del-${r.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
        <form onSubmit={invite} className="flex flex-col sm:flex-row gap-2">
          <Input
            type="email" value={email} maxLength={254}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={full ? "Maximum reached. Remove one to invite another." : "name@example.com"}
            disabled={full || !!busy || !data.email_configured}
            className="rounded-none"
            aria-label="Invite email"
            data-testid="co-owner-email-input"
          />
          <label className="sr-only" htmlFor="invite-role">Invite role</label>
          <select
            id="invite-role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            disabled={full || !!busy || !data.email_configured}
            className="text-sm border border-border bg-background px-2 py-2 rounded-none"
            data-testid="co-owner-role-select"
          >
            {ROLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <Button type="submit" className="rounded-none" disabled={full || !!busy || !email.trim() || !data.email_configured}>
            {busy === "invite" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invite"}
          </Button>
        </form>
      </div>
    </div>
  );
}
